/**
 * The room "Plan": `task_status` context bullets hold JSON envelopes
 * (`{ title?, updated_reason?, steps: [{ id, label, status, notes }] }`) that are
 * merged chronologically — the client mirror of `checklist::merge` on the server.
 * Bullets that are not JSON objects (e.g. notes added by people) stay notes.
 */
import type { RoomContextBullet } from "@/api/generated/RoomContextBullet"

export interface PlanStep {
  id: string
  label: string
  /** Lower-cased status (`pending` when missing). */
  status: string
  notes: string
}

export interface Plan {
  title: string | null
  updatedReason: string | null
  steps: PlanStep[]
  /** Free-text `task_status` bullets that are not envelopes. */
  notes: RoomContextBullet[]
  /** Bullets in any other category. */
  other: RoomContextBullet[]
}

function parseEnvelope(text: string): Record<string, unknown> | null {
  const raw = text.trim()
  if (!raw.startsWith("{")) return null
  try {
    const v: unknown = JSON.parse(raw)
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const asText = (v: unknown) =>
  v == null ? "" : typeof v === "string" ? v : typeof v === "object" ? JSON.stringify(v) : String(v)

export function mergePlan(bullets: RoomContextBullet[]): Plan {
  const plan: Plan = { title: null, updatedReason: null, steps: [], notes: [], other: [] }
  const order: string[] = []
  const steps = new Map<string, Record<string, unknown>>()
  for (const b of [...bullets].sort((a, c) => a.id - c.id)) {
    if (b.category !== "task_status") {
      plan.other.push(b)
      continue
    }
    const doc = parseEnvelope(b.text)
    if (!doc) {
      plan.notes.push(b)
      continue
    }
    if (typeof doc.title === "string") plan.title = doc.title
    if (typeof doc.updated_reason === "string") plan.updatedReason = doc.updated_reason
    if (!Array.isArray(doc.steps)) continue
    for (const item of doc.steps) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue
      const rec = item as Record<string, unknown>
      const id = asText(rec.id).trim()
      if (!id) continue
      if (!steps.has(id)) {
        order.push(id)
        steps.set(id, {})
      }
      Object.assign(steps.get(id)!, rec)
    }
  }
  plan.steps = order.map((id) => {
    const s = steps.get(id)!
    return {
      id,
      label: asText(s.label) || asText(s.title) || id,
      status: (asText(s.status).trim() || "pending").toLowerCase(),
      notes: asText(s.notes).trim(),
    }
  })
  return plan
}
